import { useEffect, useState, type ReactElement } from 'react';

import { pillButton } from '../components/pillButton.js';
import { TrackTile } from '../components/TrackTile.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { usePartyExport } from '../hooks/usePartyExport.js';
import { downloadText } from '../lib/download.js';
import {
  exportJson,
  playlistCsv,
  playlistCsvFilename,
  playlistTracks,
  playsJsonFilename,
  spotifyLinks,
} from '../lib/playlistExport.js';

import type { PartyExport, PartyExportPlay, PartySlug } from '@mixwire/contracts';

type ExportPageProps = {
  readonly slug: PartySlug;
};

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_FEEDBACK_MS = 2000;

const PARTY_DATE = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' });

const PLAY_TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const COPY_LABELS: Readonly<Record<CopyState, string>> = {
  idle: 'Copy Spotify links',
  copied: 'Copied',
  failed: 'Copy failed',
};

const plural = (count: number, noun: string): string =>
  `${String(count)} ${noun}${count === 1 ? '' : 's'}`;

const Notice = ({
  heading,
  detail,
}: {
  readonly heading: string;
  readonly detail: string;
}): ReactElement => (
  <div className="flex flex-col items-center gap-3 py-16 text-center">
    <p className="text-muted text-xs tracking-widest uppercase">The night, as a playlist</p>
    <h1 className="font-display text-content text-4xl sm:text-5xl">{heading}</h1>
    <p className="text-muted text-sm">{detail}</p>
  </div>
);

const useCopyState = (): readonly [CopyState, (next: CopyState) => void] => {
  const [state, setState] = useState<CopyState>('idle');

  useEffect(() => {
    if (state === 'idle') {
      return;
    }
    const timer = setTimeout(() => {
      setState('idle');
    }, COPY_FEEDBACK_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [state]);

  return [state, setState];
};

const ExportActions = ({
  slug,
  partyExport,
}: {
  readonly slug: PartySlug;
  readonly partyExport: PartyExport;
}): ReactElement => {
  const [copyState, setCopyState] = useCopyState();
  const links = spotifyLinks(partyExport.plays);

  const handleCopy = (): void => {
    const copy = async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(links);
        setCopyState('copied');
      } catch {
        setCopyState('failed');
      }
    };
    void copy();
  };

  return (
    <div className="bg-surface rounded-card border-subtle flex flex-col gap-3 border px-5 py-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleCopy}
          disabled={links === ''}
          className={pillButton('accent')}
        >
          {COPY_LABELS[copyState]}
        </button>
        <button
          type="button"
          onClick={() => {
            downloadText(
              playlistCsvFilename(slug),
              playlistCsv(partyExport.plays),
              'text/csv;charset=utf-8',
            );
          }}
          className={pillButton('quiet-strong')}
        >
          Download CSV
        </button>
        <button
          type="button"
          onClick={() => {
            downloadText(playsJsonFilename(slug), exportJson(partyExport), 'application/json');
          }}
          className={pillButton('quiet')}
        >
          Download JSON
        </button>
      </div>
      <p className="text-muted text-xs">
        Quickest route: copy the links, open a new playlist in the Spotify desktop app and paste —
        every track lands in one go. The CSV suits importers like TuneMyMusic or Soundiiz.
      </p>
    </div>
  );
};

const PlayRow = ({ play }: { readonly play: PartyExportPlay }): ReactElement => (
  <li className="border-subtle flex items-center gap-3 border-b px-5 py-3 last:border-b-0">
    <span className="text-muted w-12 shrink-0 text-xs tabular-nums">
      {PLAY_TIME.format(new Date(play.startedAt))}
    </span>
    <TrackTile title={play.name} colour={undefined} imageUrl={play.artUrl} size={40} />
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="text-content truncate text-sm font-semibold">{play.name}</span>
      <span className="text-secondary truncate text-xs">
        {play.artists.join(', ')} · {play.album}
      </span>
    </span>
    {play.dj === null ? null : (
      <span className="text-muted hidden max-w-40 shrink-0 truncate text-xs sm:block">
        {play.dj}
      </span>
    )}
  </li>
);

const ExportView = ({
  slug,
  partyExport,
}: {
  readonly slug: PartySlug;
  readonly partyExport: PartyExport;
}): ReactElement => {
  const { party, plays } = partyExport;

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <p className="text-muted text-xs tracking-widest uppercase">Export</p>
        <h1 className="font-display text-content text-4xl">{party.displayName}</h1>
        <p className="text-secondary text-sm">
          {PARTY_DATE.format(new Date(party.createdAt))} · {plural(plays.length, 'play')} ·{' '}
          {plural(playlistTracks(plays).length, 'unique track')}
        </p>
      </header>

      {plays.length === 0 ? (
        <Notice heading="Nothing played" detail="No track was heard long enough to log." />
      ) : (
        <>
          <ExportActions slug={slug} partyExport={partyExport} />
          <ol aria-label="Every play" className="bg-surface rounded-card border-subtle border">
            {plays.map((play) => (
              <PlayRow key={`${play.startedAt}-${play.trackId ?? play.name}`} play={play} />
            ))}
          </ol>
        </>
      )}
    </section>
  );
};

export const ExportPage = ({ slug }: ExportPageProps): ReactElement => {
  const partyExport = usePartyExport(slug);

  usePageTitle(partyExport.data?.party.displayName ?? 'Export');

  if (partyExport.data !== undefined) {
    return <ExportView slug={slug} partyExport={partyExport.data} />;
  }

  return partyExport.isError ? (
    <Notice heading="No export" detail="That party does not exist, or has nothing to show yet." />
  ) : (
    <Notice heading="Export" detail="Gathering every play…" />
  );
};
